const THREE = require('three');

const defaultVertex = /* glsl */ `
    varying vec2 vUv;
    void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0, 1);
    }
`;

const defaultFragment = /* glsl */ `
    uniform sampler2D tMap;
    varying vec2 vUv;
    void main() {
        gl_FragColor = texture2D(tMap, vUv);
    }
`;


class Post {

    constructor({ width, height, dpr = 1 }) {
        this.width = width;
        this.height = height;
        this.dpr = dpr;

        this.passes = [];

        this.geometry = new THREE.BufferGeometry();
        this.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
        this.geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 2, 0, 0, 2]), 2));

        this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -10000, 10000);
        this.scene = new THREE.Scene();

        this.mesh = new THREE.Mesh(this.geometry);
        this.scene.add(this.mesh);

        let fbo = this.fbo = {
            read: null,
            write: null,
            swap: () => {
                let temp = fbo.read;
                fbo.read = fbo.write;
                fbo.write = temp;
            }
        };  
        
        this.fbo.read = new THREE.WebGLRenderTarget(1, 1);
        this.fbo.read.texture.name = 'rt1';
        this.fbo.write = new THREE.WebGLRenderTarget(1, 1);
        this.fbo.write.texture.name = 'rt2';

        this.resize({ width, height, dpr });
    }

    addPass({ 
        vertexShader = defaultVertex, 
        fragmentShader = defaultFragment, 
        uniforms = {}, 
        defines = {},
        textureUniform = 'tMap', 
        enabled = true,
        target = null,
    } = {}) {
        uniforms[textureUniform] = { value: this.fbo.read.texture };

        const material = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms,
            defines,
            depthWrite: false,
            depthTest: false,
        });

        const pass = {
            material,
            enabled,
            textureUniform,
            target,
        };

        this.passes.push(pass);

        return pass;
    }

    resize({ width, height, dpr = this.dpr }) {
        let w = width * dpr;
        let h = height * dpr;

        this.fbo.read.setSize(w, h);
        this.fbo.write.setSize(w, h);
    }

    render(renderer, scene, camera, target = null) {
        const enabledPasses = this.passes.filter(pass => pass.enabled);

        if (enabledPasses.length > 0) {
            renderer.setRenderTarget(this.fbo.write);
        } else {
            renderer.setRenderTarget(null);
        }

        renderer.render(scene, camera);
        renderer.setRenderTarget(null);

        this.fbo.swap();
        
        enabledPasses.forEach((pass, i) => {
            pass.material.uniforms[pass.textureUniform].value = this.fbo.read.texture;
            this.mesh.material = pass.material;

            if (i === enabledPasses.length - 1) {
                if (target) {
                    renderer.setRenderTarget(target);
                } else if (pass.target) {
                    renderer.setRenderTarget(pass.target);
                } else {
                    renderer.setRenderTarget(null);
                }
            } else {
                if (pass.target) {
                    renderer.setRenderTarget(pass.target);
                } else {
                    renderer.setRenderTarget(this.fbo.write);
                }
            }

            if (i === enabledPasses.length - 1) {
                renderer.clear(renderer.autoClearColor, renderer.autoClearDepth, renderer.autoClearStencil);
            }

            renderer.render(this.scene, this.camera);
            this.fbo.swap();
        });
    }
    
    clear(renderer) {
        renderer.clear(renderer.autoClearColor, renderer.autoClearDepth, renderer.autoClearStencil);
    }

    dispose() {
        this.scene.remove(this.mesh);
        this.mesh.geometry.dispose();
        this.mesh.geometry = null;
        this.mesh.material.dispose();
        this.mesh.material = null;

        if (this.fbo.read) {
            this.fbo.read = null;
        }

        if (this.fbo.write) {
            this.fbo.write = null;
        }

        this.fbo = null;
        this.passes = null;
    }

}

export default Post;